<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor';
    let nextLink= '/Documentation/Defining_an_Editor/Indentation';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="projections-1"
  bind:intersecting={visibleById["projections-1"]}
  onIntersectChange={(visible) => setVisible("projections-1", visible)}
>Projections</SectionComponent>
<p>A projection is defined using angular brackets,
in a style similar to Markdown. The appearance of the definition
closely resembles the resulting output. Everything within square
brackets (<code>[]</code>), except text enclosed in <code>$&#123;&#125;</code>, is interpreted
literally, including the indentation. For more details, refer to the information
on <a
  href="/Documentation/Defining_an_Editor/Indentation"
>Indentation</a>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-indentation.edit#L6-L14

Text &#123;
[
This is
        literal text
      that is projected in the
                editor
  for every concept of type Text.
]
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="including-properties-2"
  bind:intersecting={visibleById["including-properties-2"]}
  onIntersectChange={(visible) => setVisible("including-properties-2", visible)}
>Including Properties</SectionComponent>
<p>When defining a projection for a concept or interface, you will likely need
to include its properties. This is done using the special notation <code>$&#123;&#125;</code>, which
instructs Freon to include a property according to the projection of its type.</p>
<p>For example, given the following metamodel:</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L22-L32

// A BaseProduct defines all the different elements ('parts') that can be
// used to create a marketable InsuranceProduct.
concept BaseProduct &#123;
    name: identifier;               // internal name
    isUnderConstruction: boolean;   // defines whether this base product is still 'raw'
    theme: InsuranceTheme;          // the 'kind' of insurance
    parts: InsurancePart[];         // all parts of this product
    // The following properties are present to show the different options for displaying booleans.
    isApprovedLevel1: boolean;
    isApprovedLevel2: boolean;
    isApprovedLevel3: boolean;</code></pre>
<p>consider the property <code>self.body</code>, which is of type <code>DocuExpression</code>. It
will be displayed based on the projection defined for <code>DocuExpression</code>.
Meanwhile, <code>self.declaredType</code> is a property of the abstract type <code>DocuType</code>.
This property will be projected according to the definition of
the specific (non-abstract) subtype of <code>DocuType</code> encountered at runtime.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-default.edit#L38-L49

InsuranceProduct &#123;[
    Insurance Product $&#123;name&#125; ( public name: $&#123;productName&#125; ) USES $&#123;basedOn horizontal separator[, ]&#125;
        Themes: $&#123;themes horizontal separator[, ]&#125;
        Premium: $&#123;advertisedPremium&#125; per $&#123;nrPremiumDays&#125;
        Insured risks:
            $&#123;parts vertical terminator [;]&#125;
        Calculation
            [? Risk adjusted by = $&#123;riskAdjustment&#125; ]
            calculated premium: $&#123;calculation&#125;
        [?Helper functions:
            $&#123;helpers vertical&#125;]
]&#125;</code></pre>
<Note> 
{#snippet header()}Only direct properties are allowed{/snippet}
{#snippet content()}
Only the properties that are directly owned by the concept or interface are allowed. For instance,
<code>self.declaredType.name</code> is not a valid property projection. 
{/snippet}
</Note>
<Note header={header2} content={content2}> 
{#snippet header2()}<code>self</code> is optional{/snippet}
{#snippet content2()}
Because you may only use direct properties, the prefix <code>self</code> may be omitted. 
{/snippet}
</Note>
<Note header={header3} content={content3}> 
{#snippet header3()} One property, one occurrence in the editor{/snippet}
{#snippet content3()}
It is not possible to show the same property of a concept twice in the same editor.
The tooling we use to keep the state of the underlying model in sync with the view in the running editor
does not allow us to do this.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="using-named-projections-3"
  bind:intersecting={visibleById["using-named-projections-3"]}
  onIntersectChange={(visible) => setVisible("using-named-projections-3", visible)}
>Using Named Projections</SectionComponent>
<p>By default, a property you include will be displayed using the projection
defined for its type. Freon determines this projection based on the
precedence specified in the <code>.edit</code> files.</p>
<p>If you want to use a specific projection from a different editor,
you can use a <strong>named property projection</strong>. In this case, Freon
will look for the projection in the editor with the specified name.</p>
<p>In the next example, the projection for <code>self.parts:comments</code> will first be searched
in the editor named <code>comments</code>. If it is not found there,
Freon will fall back to the standard precedence order of projections.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-named-proj.edit#L3-L8

BaseProduct &#123;[
    /* In this projection 'self.parts' is always shown according to the projection */
    /* defined for concept InsurancePart in the editor 'comments'.                 */
    Base Products $&#123;self.name&#125; for $&#123;self.theme&#125;
        $&#123;self.parts:comments&#125;
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="lists-4"
  bind:intersecting={visibleById["lists-4"]}
  onIntersectChange={(visible) => setVisible("lists-4", visible)}
>Lists</SectionComponent>
<p>For list properties, you can specify horizontal or vertical projection. Both options are optional. If
neither <code>vertical</code> nor <code>horizontal</code> is specified, the property
will be displayed as a vertical list by default.</p>
<p>You can also choose to project a list property
as a <a
  href="/Documentation/Defining_an_Editor/Projections#tables-5"
>table</a>.</p>
<p>For a list, you can include the following options:</p>
<ul>
<li>A <strong>separator</strong> string, which will appear between each element.</li>
<li>A <strong>terminator</strong> string, which will appear after each element.</li>
<li>An <strong>initiator</strong> string, which will appear before each element.</li>
</ul>
<p>All of these are optional. The default separator is a single space.</p>
<p>In the following example, the list <code>parts</code> is displayed vertically
with a <code>';'</code> terminator. The list <code>themes</code> is displayed horizontally
with a <code>', '</code> separator. The list <code>helpers</code> is shown as a vertical
list without any separator, terminator, or initiator. In fact,
the <code>vertical</code> keyword could be omitted for <code>helpers</code>, as it is the
default projection for lists.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-default.edit#L38-L49

InsuranceProduct &#123;[
    Insurance Product $&#123;name&#125; ( public name: $&#123;productName&#125; ) USES $&#123;basedOn horizontal separator[, ]&#125;
        Themes: $&#123;themes horizontal separator[, ]&#125;
        Premium: $&#123;advertisedPremium&#125; per $&#123;nrPremiumDays&#125;
        Insured risks:
            $&#123;parts vertical terminator [;]&#125;
        Calculation
            [? Risk adjusted by = $&#123;riskAdjustment&#125; ]
            calculated premium: $&#123;calculation&#125;
        [?Helper functions:
            $&#123;helpers vertical&#125;]
]&#125;</code></pre>
<Note header={header4} content={content4}> 
{#snippet header4()}Newlines in terminator or separator are ignored{/snippet}
{#snippet content4()}
A terminator or separator should not contain a newline character. Horizontal or vertical layout is
purely determined by the keywords <code>horizontal</code> and <code>vertical</code>.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="tables-5"
  bind:intersecting={visibleById["tables-5"]}
  onIntersectChange={(visible) => setVisible("tables-5", visible)}
>Tables</SectionComponent>
<p>If a property is a list, you can choose to display it as a table.
Tables can be either row-based or column-based. In a row-based table,
each element of the list is displayed in a separate row, while
in a column-based table, each element is displayed in a single
column. The default is row-based.</p>
<p>Defining a table involves two steps:</p>
<ol>
<li>Add the keyword <code>table</code> to the list property you want to
display as a table. Optionally, you can also specify either <code>rows</code> or <code>columns</code>.</li>
<li>Define a table-projection for the type of the elements
in the list. This projection specifies the table headers and </li>
<li>determines how the elements of the list are arranged in rows or columns.</li>
</ol>
<Note content={content5}> 
{#snippet content5()}
Note that you only need to include one
table projection for the elements for both column and row based tables.
Freon will swap the entries when needed.
{/snippet}
</Note>
<p>To project the <code>parts</code> property of concept <code>BaseProduct</code> as a row based table,
you can use the following code.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-tables.edit#L13-L16

BaseProduct &#123;[
    Base Products $&#123;name&#125; for $&#123;theme&#125;
        $&#123;parts table rows&#125;
]&#125;</code></pre>
<p>Given the above example, there should also be a projection tagged <code>table</code> for the
concept <code>InsurancePart</code> (the type of <code>parts</code>).
The example below defines four columns or rows, each with its own header.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-tables.edit#L6-L11

InsurancePart&#123;
table [
    Name    | risk               | pay out          | is approved
    $&#123;name&#125; | $&#123;statisticalRisk&#125; | $&#123;maximumPayOut&#125; | $&#123;isApproved&#125;
]
&#125;</code></pre>
<Note header={header6} content={content6}> 
{#snippet header6()}Properties within a table are displayed according to their own projection{/snippet}
{#snippet content6()}
Each of the properties in a table is displayed using its own projection.
In this example, <code>name</code> is a string, and will be displayed as an editable text,
and <code>maximumPayout</code> is a <code>EuroLiteral</code>
Note that the properties can be lists themselves, which can be displayed as lists or tables.
{/snippet}
</Note>
<Note header={header7} content={content7}> 
{#snippet header7()}Whitespace in headers is ignored{/snippet}
{#snippet content7()}
    Whitespace between headers is ignored, but aligning columns improves readability.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="optional-projections-6"
  bind:intersecting={visibleById["optional-projections-6"]}
  onIntersectChange={(visible) => setVisible("optional-projections-6", visible)}
>Optional Projections</SectionComponent>
<p>When a property is marked optional in the language structure definition (the <code>.ast</code> files), the projection
of this property should also be optional. This is indicated by <code>[?</code>.</p>
<p>In the next example both the property <code>riskAdjustment</code> and <code>helpers</code> are only shown if they are present.
If they are not present, respectively the text <code>Risk adjusted by =</code> or <code>Helper functions:</code> is omitted as well.</p>
<p>Note that optional projections for non-optional properties are not allowed.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-default.edit#L38-L49

InsuranceProduct &#123;[
    Insurance Product $&#123;name&#125; ( public name: $&#123;productName&#125; ) USES $&#123;basedOn horizontal separator[, ]&#125;
        Themes: $&#123;themes horizontal separator[, ]&#125;
        Premium: $&#123;advertisedPremium&#125; per $&#123;nrPremiumDays&#125;
        Insured risks:
            $&#123;parts vertical terminator [;]&#125;
        Calculation
            [? Risk adjusted by = $&#123;riskAdjustment&#125; ]
            calculated premium: $&#123;calculation&#125;
        [?Helper functions:
            $&#123;helpers vertical&#125;]
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="inherited-projections-7"
  bind:intersecting={visibleById["inherited-projections-7"]}
  onIntersectChange={(visible) => setVisible("inherited-projections-7", visible)}
>Inherited Projections</SectionComponent>
<p>Just as concepts can inherit from other concepts, projection definitions can also inherit from their super concepts.
To indicate the inclusion the syntax <code>[=&gt; SUPER]</code> is used, where <code>SUPER</code> is the name of the super concept.</p>
<p>For instance, in the UML metamodel both <code>AssociationClass</code> and <code>Class</code> inherit from the abstract <code>Classifier</code>. When building an editor for the UML metamodel,
one may define the projections as follows. The entries for <code>attributes</code>, <code>operations</code>, and <code>states</code> will appear for both child concepts.</p>
<pre class="language-freon"><code class="language-freon">Classifier &#123;
        [
        [?&lt;attributes&gt;  $&#123;self.attributes vertical terminator [;] &#125;]
        [?&lt;operations&gt;  $&#123;self.operations vertical terminator [;] &#125;]
        [?&lt;states&gt;      $&#123;self.states     vertical separator [;] &#125;]
        ]
&#125;

AssociationClass &#123;
        [
        [?$&#123;self.visibility&#125;] &lt;associationclass&gt; $&#123;self.name&#125;
        $&#123;self.end1&#125; &lt;-&gt; $&#123;self.end2&#125;
        
        [=&gt; Classifier ]
        &lt;endassociationclass&gt;
        ]
&#125;

Class &#123;
    [
    [?$&#123;visibility&#125;] $&#123;self.isAbstract [&lt;abstract&gt;]&#125; &lt;class&gt; $&#123;self.name&#125;
    [?&lt;specializes&gt;  $&#123;self.generalizations horizontal separator [, ] &#125;]
    [?&lt;implements&gt;   $&#123;self.interfaces      horizontal separator [, ] &#125;]
    
    [=&gt; Classifier]
    &lt;endclass&gt;
    ]
&#125;</code></pre>


<PrevNextSection {prevLink} {nextLink} />